import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, FileText, Sliders, ShoppingCart, CheckCircle2, Truck } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const TYPE_META = {
  count:    { label: 'Count',    Icon: CheckCircle2, tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  adjust:   { label: 'Adjust',   Icon: Sliders,      tone: 'bg-slate-50   text-slate-800   border-slate-200'   },
  sale:     { label: 'Sale',     Icon: ShoppingCart, tone: 'bg-amber-50   text-amber-900   border-amber-200'   },
  delivery: { label: 'Delivery', Icon: Truck,        tone: 'bg-blue-50    text-blue-900    border-blue-200'    },
};

const fmtGBP = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(n || 0));
const fmtMethod = (m) => ({
  bank_transfer: 'Bank Transfer', epos: 'EPOS', stripe: 'Stripe', worldpay: 'WorldPay', cash: 'Cash',
}[m] || m || '—');
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export default function StockMovements() {
  const navigate = useNavigate();
  const { isStaff } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/movements?limit=500`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load movements');
      setRows(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isStaff) { navigate('/'); return; }
    load();
  }, [isStaff, navigate, load]);

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff/stock')} data-testid="movements-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back to Stock">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate"><FileText className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> Stock Movements</h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Audit log — counts · adjustments · sales (newest first)</p>
          </div>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" aria-label="Staff">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-4 py-5">
        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-gray-500 italic">— no movements yet —</div>
        ) : (
          <Card className="divide-y divide-gray-100">
            {rows.map(r => {
              const meta = TYPE_META[r.type] || TYPE_META.adjust;
              const Icon = meta.Icon;
              return (
                <div key={r.id} data-testid={`movement-row-${r.id}`} className="p-4 flex flex-wrap items-start gap-3">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border inline-flex items-center gap-1 shrink-0 ${meta.tone}`}>
                    <Icon className="w-3 h-3" /> {meta.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{r.product_name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {r.category} · {fmtDate(r.created_at)} · by {r.performed_by_name || '—'}
                    </div>
                    {r.type === 'sale' && (
                      <div className="text-xs text-gray-700 mt-1">
                        <strong>{fmtGBP(r.sale_value)}</strong> · {fmtMethod(r.payment_method)}
                        {r.invoice_number ? ` · INV ${r.invoice_number}` : ''}
                      </div>
                    )}
                    {r.type === 'delivery' && (r.supplier || r.reference) && (
                      <div className="text-xs text-gray-700 mt-1">
                        {r.supplier ? <strong>{r.supplier}</strong> : null}
                        {r.supplier && r.reference ? ' · ' : ''}
                        {r.reference ? `Ref ${r.reference}` : ''}
                      </div>
                    )}
                    {r.notes && <div className="text-xs italic text-gray-500 mt-0.5">&quot;{r.notes}&quot;</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase text-gray-500">{r.delta > 0 ? 'Added' : r.delta < 0 ? 'Removed' : 'No change'}</div>
                    <div className={`text-lg font-bold ${r.delta > 0 ? 'text-emerald-700' : r.delta < 0 ? 'text-rose-700' : 'text-gray-700'}`}>
                      {r.delta > 0 ? `+${r.delta}` : r.delta}
                    </div>
                    <div className="text-[11px] text-gray-500">→ {r.qty_after} on hand</div>
                  </div>
                </div>
              );
            })}
          </Card>
        )}
      </div>
    </div>
  );
}
